// components/tables/cellRef.ts
// Shared A1 <-> "row,col" cell-reference helpers for the Analyst spreadsheet — one
// source of truth used by the agent context, the Analyst commands, and the grid
// (citations / range read / formatting). Pure functions, no deps.
//
// It also owns `parseCellPatch`, the reader for the `cells` parameter that the two
// write commands share. That parser lived inline in each of them until one copy
// silently rejected the absolute refs the other accepted; keeping it here means an
// address is read the same way wherever the agent aims it.

/** 0-based column index -> letters: 0 -> "A", 25 -> "Z", 26 -> "AA". */
export function colLetter(c: number): string {
  let s = '';
  let x = c + 1;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

/** Column letters -> 0-based index: "A" -> 0, "AA" -> 26. Returns -1 on bad input. */
export function colToIndex(letters: string): number {
  if (!letters) return -1;
  let col = 0;
  for (const ch of letters.toUpperCase()) {
    const code = ch.charCodeAt(0) - 64;
    if (code < 1 || code > 26) return -1;
    col = col * 26 + code;
  }
  return col - 1;
}

/** "row,col" (0-based) -> "A1". */
export function keyToA1(key: string): string {
  const [r, c] = key.split(',');
  return `${colLetter(Number(c))}${Number(r) + 1}`;
}

/** "B3" (or "$B$3") -> "2,1" (0-based "row,col"), or null if not a single-cell ref. */
export function a1ToKey(a1: string): string | null {
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(a1.trim());
  if (!m || !m[1] || !m[2]) return null;
  const col = colToIndex(m[1]);
  const row = parseInt(m[2], 10) - 1;
  if (row < 0 || col < 0) return null;
  return `${row},${col}`;
}

export interface A1Range {
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

/** Parse "B2:D5" or a single "B2" into 0-based inclusive bounds (normalized), or null. */
export function parseA1Range(s: string): A1Range | null {
  const parts = s.trim().split(':');
  if (parts.length === 1) {
    const k = a1ToKey(parts[0]!);
    if (!k) return null;
    const [r, c] = k.split(',').map(Number);
    return { r0: r!, c0: c!, r1: r!, c1: c! };
  }
  if (parts.length !== 2) return null;
  const k0 = a1ToKey(parts[0]!);
  const k1 = a1ToKey(parts[1]!);
  if (!k0 || !k1) return null;
  const [ar, ac] = k0.split(',').map(Number);
  const [br, bc] = k1.split(',').map(Number);
  return { r0: Math.min(ar!, br!), c0: Math.min(ac!, bc!), r1: Math.max(ar!, br!), c1: Math.max(ac!, bc!) };
}

export interface CellPatch {
  /** "row,col" -> value to write. */
  cellMap: Record<string, string>;
  /** "row,col" -> the agent's stated reason, when it passed one (ANALYST_WRITE_CELLS only). */
  whyByKey: Record<string, string>;
  /** Entries whose address could not be read, verbatim, so the caller can name them back. */
  rejected: string[];
}

/** Short, quote-free label for an entry we could not read, for an error message. */
function patchEntryLabel(entry: unknown): string {
  const raw =
    typeof entry === 'string' ? entry : (() => {
      try {
        return JSON.stringify(entry) ?? String(entry);
      } catch {
        return String(entry);
      }
    })();
  return raw.length > 40 ? `${raw.slice(0, 40)}…` : raw;
}

/**
 * Reads the `cells` parameter of the agent's two write commands into a "row,col" map.
 *
 * Addresses go through `a1ToKey`, so "$B$3" lands on B3 like a formula would — a target
 * address and a formula reference are written by the same agent in the same turn, and
 * having only one of them accept the dollar form is a trap, not a guard.
 *
 * Unreadable entries are collected rather than dropped. The caller is expected to name
 * them: a patch that half-lands and reports a success is how a model ends up with a hole
 * in it and nobody looking.
 */
export function parseCellPatch(cells: unknown): CellPatch {
  const cellMap: Record<string, string> = {};
  const whyByKey: Record<string, string> = {};
  const rejected: string[] = [];

  for (const entry of Array.isArray(cells) ? cells : []) {
    if (!entry || typeof entry !== 'object') {
      rejected.push(patchEntryLabel(entry));
      continue;
    }
    const c = entry as Record<string, unknown>;
    let key: string | null = null;
    if (typeof c.cell === 'string') key = a1ToKey(c.cell);
    else if (Number.isFinite(Number(c.row)) && Number.isFinite(Number(c.col))) {
      key = `${Math.max(0, Math.round(Number(c.row)))},${Math.max(0, Math.round(Number(c.col)))}`;
    }
    if (!key) {
      rejected.push(patchEntryLabel(typeof c.cell === 'string' ? c.cell : c));
      continue;
    }
    cellMap[key] = c.value === null || c.value === undefined ? '' : String(c.value);
    if (typeof c.why === 'string' && c.why.trim()) whyByKey[key] = c.why.trim();
  }

  return { cellMap, whyByKey, rejected };
}
